export const DOCUMENT_SCAN_ENTRY_POINTS = [
  "CASE_CHAT",
  "FA_WIZARD",
  "POST_ESCALATION",
  "HOME",
  "OTHER",
] as const;

/** Where the user opened the native document scanner from. */
export type DocumentScanEntryPoint =
  (typeof DOCUMENT_SCAN_ENTRY_POINTS)[number];

const SCAN_QUALITY_EXPERIMENT_SURFACES = [
  "CASE_CHAT",
  "POST_ESCALATION",
  "HOME",
] as const satisfies readonly DocumentScanEntryPoint[];

/**
 * Whether scans from this surface participate in the scan experiments (the
 * quality gate and capture confirmation share one scope, because
 * `skipLaunchDarkly` is per event and can't hold two).
 * Client enrollment, client metric fan-out, and the server-side downstream
 * guardrail emit must all key on this one predicate, or the experiment is
 * silently biased.
 *
 * Default-deny by design: the server receives the entry point as a raw
 * string off the wire, so anything unrecognised — a new surface, a stale
 * client, an absent value — is excluded until added here. FA_WIZARD is
 * excluded because FA documents prefill application fields and keep the
 * strict GOOD gate; OTHER is excluded so a surface that forgets to pass an
 * entry point under-enrolls rather than contaminates.
 */
export const isScanQualityExperimentSurface = (
  entryPoint: string | null | undefined,
): boolean =>
  SCAN_QUALITY_EXPERIMENT_SURFACES.some(surface => surface === entryPoint);

/**
 * Collapse a batch of per-file entry points to a single provenance value, or
 * `undefined` if the batch isn't unanimous. Keeps the "a mixed batch degrades
 * to excluded rather than inheriting file[0]'s provenance" invariant in one
 * place — the client upload provider and the home-upload flow both derive from
 * it so they can't drift.
 */
export const deriveBatchScanbotEntryPoint = (
  entryPoints: (DocumentScanEntryPoint | undefined)[],
): DocumentScanEntryPoint | undefined => {
  const unique = new Set(entryPoints);
  return unique.size === 1 ? entryPoints[0] : undefined;
};
